/**
 * OfflineIndicator - Indicador visual de estado offline/online
 * 
 * FUT-04: Cache tiles offline con Service Worker
 * 
 * Muestra:
 * - Estado de conexión (online/offline)
 * - Actualización disponible del SW
 * - Opción de actualizar
 */

import { WifiHigh, WifiSlash, ArrowsClockwise } from '@phosphor-icons/react'
import { useOfflineStatus } from '../hooks/useOfflineStatus'
import { cn } from '../lib/utils'

interface OfflineIndicatorProps {
  /** Mostrar siempre o solo cuando offline */
  showWhenOnline?: boolean
  /** Posición en pantalla */
  position?: 'top-right' | 'bottom-right' | 'bottom-left'
  className?: string
}

export default function OfflineIndicator({
  showWhenOnline = false,
  position = 'bottom-right',
  className
}: OfflineIndicatorProps) {
  const { isOnline, swActive, swUpdateAvailable, updateServiceWorker } = useOfflineStatus()

  // No mostrar si está online y no hay que mostrarlo siempre
  if (isOnline && !showWhenOnline && !swUpdateAvailable) {
    return null
  }

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  }

  return (
    <div 
      className={cn(
        'fixed z-50 flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg text-sm font-medium transition-all',
        positionClasses[position],
        isOnline 
          ? 'bg-green-500/90 text-white' 
          : 'bg-amber-500/90 text-white animate-pulse',
        className
      )}
    >
      {isOnline ? (
        <>
          <WifiHigh size={18} weight="bold" />
          <span>Online</span>
          {swActive && (
            <span className="text-xs opacity-75 ml-1">(cache activo)</span>
          )}
        </>
      ) : (
        <>
          <WifiSlash size={18} weight="bold" />
          <span>Sin conexión</span>
          {swActive && (
            <span className="text-xs opacity-75 ml-1">(usando cache)</span>
          )}
        </>
      )}

      {/* Botón de actualización si hay nueva versión */}
      {swUpdateAvailable && (
        <button
          onClick={updateServiceWorker}
          className="ml-2 p-1 rounded bg-white/20 hover:bg-white/30 transition-colors"
          title="Nueva versión disponible - Clic para actualizar"
        >
          <ArrowsClockwise size={16} weight="bold" className="animate-spin" />
        </button>
      )}
    </div>
  )
}

/**
 * Banner compacto para mostrar en header
 */
export function OfflineBanner() {
  const { isOnline, swUpdateAvailable, updateServiceWorker } = useOfflineStatus()

  if (isOnline && !swUpdateAvailable) return null

  return (
    <div 
      className={cn(
        'w-full py-1 px-4 text-center text-xs font-medium',
        isOnline 
          ? 'bg-blue-500 text-white'
          : 'bg-amber-500 text-white'
      )}
    >
      {!isOnline && (
        <span className="flex items-center justify-center gap-2">
          <WifiSlash size={14} />
          Modo offline - Los mapas visitados están disponibles en cache
        </span>
      )}
      {swUpdateAvailable && isOnline && (
        <button 
          onClick={updateServiceWorker}
          className="flex items-center justify-center gap-2 w-full hover:underline"
        >
          <ArrowsClockwise size={14} />
          Nueva versión disponible - Clic para actualizar
        </button>
      )}
    </div>
  )
}
